import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Collapse,
    Card,
    CardHeader,
    CardBody,
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input
} from 'reactstrap';
import Switch from 'react-switch';
import { FaChevronDown, FaChevronRight, FaEdit } from 'react-icons/fa';
import {
    showSuccessAlert,
    showLoadingAlert,
    showErrorAlert
} from "../../../components/custom/sweetalertHelper";
import { getSubscriptionList } from '../../../slices/veloxity/auth/permissionsManagement/getSubscriptionList';
import { updateSubscription } from '../../../slices/veloxity/auth/permissionsManagement/updateSubscription';
import { enableDisableSubscription } from '../../../slices/veloxity/auth/permissionsManagement/enableDisableSubscription';
import Loader from '../../../components/common/Loader';
import { packages } from './packages';

const CompanyPermissions = ({ clientId }) => {
    const dispatch = useDispatch();
    const { subscriptions, loading, error } = useSelector((state) => state.getSubscriptionList);
    const [permissions, setPermissions] = useState([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPermission, setCurrentPermission] = useState(null);
    const [openGroups, setOpenGroups] = useState({});
    const [currentPackage, setCurrentPackage] = useState(null);
    const [editPackage, setEditPackage] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null);

    useEffect(() => {
        dispatch(getSubscriptionList(clientId));
    }, [dispatch, clientId]);

    useEffect(() => {
        if (subscriptions && subscriptions.length > 0) {
            const formattedPermissions = subscriptions.flatMap(sub =>
                Object.keys(sub.featureFlags).map(flag => ({
                    id: `${sub.id}-${flag}`,
                    group: sub.subscriptionType,
                    name: flag,
                    enabled: sub.featureFlags[flag], // Use the feature flag status from the subscription
                    subscriptionId: sub.id,
                    quota: sub.quota,
                    schedule: sub.schedule,
                    packageType: sub.packageType,
                    exceedable: sub.exceedable || false,
                    featureFlags: sub.featureFlags
                }))
            );
            setPermissions(formattedPermissions);
        }
    }, [subscriptions]);

    // featureFlags dizisini güncellemek için yardımcı fonksiyon
    const updateFeatureFlags = (featureFlags, flag, isEnabled) => {
        const updatedFlags = { ...featureFlags };
        if (isEnabled) {
            updatedFlags[flag] = true;  
        } else {
            delete updatedFlags[flag];  
        }
        return Object.keys(updatedFlags).filter(key => updatedFlags[key] === true);
    };
    


    const handleFeatureFlagToggle = async (permission) => {
        if (!permission) return;

        setIsLoading(true);
        showLoadingAlert('Updating Feature Flag', 'Please wait...');

        const { subscriptionId, name, enabled, quota, schedule, packageType, featureFlags, exceedable } = permission;
        const updatedEnabled = !enabled;


        const updatedFeatureFlags = updateFeatureFlags(featureFlags, name, updatedEnabled);
        const updateData = {
            quota,
            schedule,
            packageType,
            featureFlags: updatedFeatureFlags,  
        };

        try {
            await dispatch(updateSubscription({
                clientId,
                subscriptionId,
                updateData
            })).unwrap();

            await dispatch(getSubscriptionList(clientId));

            setIsLoading(false);
            showSuccessAlert("Feature flag updated successfully!");
        } catch (error) {
            setIsLoading(false);
            showErrorAlert("Failed to update feature flag. Please try again.");
        }
    };

    // Handler for toggling package enable/disable
    const handlePackageSwitchChange = (pkg, isEnabled) => {
        setCurrentPackage(pkg);
        setConfirmAction(() => () => handleTogglePackage(pkg, isEnabled));
        setIsConfirmModalOpen(true);
    };

    const handleTogglePackage = async (pkg, isEnabled) => {
        setIsLoading(true);
        showLoadingAlert(isEnabled ? 'Enabling Package' : 'Disabling Package', 'Please wait...');

        try {
            const subscription = subscriptions.find(sub => sub.subscriptionType === pkg.name);
            if (subscription) {
                await dispatch(enableDisableSubscription({
                    clientId,
                    subscriptionId: subscription.id
                })).unwrap();
            }

            await dispatch(getSubscriptionList(clientId));

            setIsLoading(false);
            setIsConfirmModalOpen(false);
            showSuccessAlert(`Package ${isEnabled ? 'enabled' : 'disabled'} successfully!`);
        } catch (error) {
            setIsLoading(false);
            setIsConfirmModalOpen(false);
            showErrorAlert(`Failed to ${isEnabled ? 'enable' : 'disable'} package. Please try again.`);
        }
    };

    const toggleGroup = (group) => {
        setOpenGroups(prevState => ({
            ...prevState,
            [group]: !prevState[group]
        }));
    };

    const isPackageEnabled = (packageName) => {
        return subscriptions.some(sub => sub.subscriptionType === packageName && sub.enabled);
    };

    const handleConfirmAction = () => {
        if (confirmAction) {
            confirmAction();
        }
        setIsConfirmModalOpen(false);
    };

    const handleEditPackage = (pkg) => {
        const subscription = subscriptions.find(sub => sub.subscriptionType === pkg.name);
        setEditPackage(subscription);
        setIsEditModalOpen(true);
    };

    // Handler for saving package updates
// Edit Package modali için handler
const handleSavePackage = async () => {
    if (!editPackage) return;

    setIsLoading(true);
    showLoadingAlert('Updating Package', 'Please wait...');


    const updatedFeatureFlags = updateFeatureFlags(editPackage.featureFlags);

    const updateData = {
        quota: editPackage.quota,
        schedule: editPackage.schedule,
        packageType: editPackage.packageType,
        featureFlags: updatedFeatureFlags, 
    };

    try {
        await dispatch(updateSubscription({
            clientId,
            subscriptionId: editPackage.id,
            updateData
        })).unwrap();


        await dispatch(getSubscriptionList(clientId));

        setIsLoading(false);
        setEditPackage(null);
        setIsEditModalOpen(false);
        showSuccessAlert("Package updated successfully!");
    } catch (error) {
        setIsLoading(false);
        showErrorAlert("Failed to update package. Please try again.");
    }
};

    return (
        <div className="company-permissions">
            {loading && <Loader />}
            {error && <div className="alert alert-danger">{error}</div>}
            {!loading && !error && (
                <Row>
                    {packages.map(pkg => {
                        const packagePermissions = permissions.filter(p => p.group === pkg.name);
                        const isEnabled = isPackageEnabled(pkg.name);
                        const hasFeatureFlags = pkg.features.length > 0;
                        const subscription = subscriptions.find(sub => sub.subscriptionType === pkg.name);
                        return (
                            <Col key={pkg.id} sm="6" md="4" lg="3" className="mb-3">
                                <Card>
                                    <CardHeader 
                                        onClick={() => hasFeatureFlags && toggleGroup(pkg.name)} 
                                        className="d-flex justify-content-between align-items-center"
                                        style={{ cursor: hasFeatureFlags ? 'pointer' : 'default' }}
                                    >
                                        <div className="d-flex align-items-center">
                                            {hasFeatureFlags && (
                                                openGroups[pkg.name] ? <FaChevronDown /> : <FaChevronRight />
                                            )}
                                            <span className="ms-2">{pkg.name.replace(/_/g, ' ')}</span>
                                        </div>
                                        <Switch
                                            onChange={() => handlePackageSwitchChange(pkg, !isEnabled)}
                                            checked={isEnabled}
                                            onColor="#00C851"
                                            offColor="#ff4444"
                                        />
                                    </CardHeader>
                                    <CardBody>
                                        <div className="package-info w-100 d-flex justify-content-between align-items-center">
                                            <div><strong>Quota</strong> {subscription?.quota || "N/A"}</div>
                                            <div><strong>Schedule</strong> {subscription?.schedule || "N/A"}</div>
                                            <div><strong>Package Type</strong> {subscription?.packageType || "N/A"}</div>
                                            <Button color="primary" size="sm" onClick={() => handleEditPackage(pkg)}>
                                                <FaEdit />
                                            </Button>
                                        </div>
                                    </CardBody>
                                    {hasFeatureFlags && (
                                        <Collapse isOpen={openGroups[pkg.name]}>
                                            <CardBody>
                                                <div className="package-details">
                                                    <Table hover responsive striped className="mb-0">
                                                        <thead className="table-light">
                                                            <tr style={{ textAlign: 'center' }}>
                                                                <th>Feature</th>
                                                                <th style={{ width: '150px' }}>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {pkg.features.map((feature) => {
                                                                const permission = permissions.find(p => p.name === feature && p.group === pkg.name);
                                                                const isFeatureEnabled = permission ? permission.enabled : false;
                                                                const isOwned = !!permission;
                                                                return (
                                                                    <tr key={feature} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                                        <td>{feature}</td>
                                                                        <td>
                                                                            <Switch
                                                                                onChange={() => handleFeatureFlagToggle(permission)}
                                                                                checked={isFeatureEnabled}
                                                                                onColor="#00C851"
                                                                                offColor="#ff4444"
                                                                                disabled={!isOwned}
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </CardBody>
                                        </Collapse>
                                    )}
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            <Modal isOpen={isEditModalOpen} toggle={() => setIsEditModalOpen(!isEditModalOpen)}>
                <ModalHeader toggle={() => setIsEditModalOpen(!isEditModalOpen)}>
                    Edit Package
                </ModalHeader>
                <ModalBody>
                    {editPackage && (
                        <Form>
                            <FormGroup>
                                <Label for="quota">Quota</Label>
                                <Input
                                    type="number"
                                    id="quota"
                                    value={editPackage.quota}
                                    onChange={(e) => setEditPackage({ ...editPackage, quota: e.target.value })}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="schedule">Schedule</Label>
                                <Input
                                    type="select"
                                    id="schedule"
                                    value={editPackage.schedule}
                                    onChange={(e) => setEditPackage({ ...editPackage, schedule: e.target.value })}
                                    className="form-control"
                                >
                                    <option value="WEEKLY">Weekly</option>
                                    <option value="MONTHLY">Monthly</option>
                                    <option value="YEARLY">Yearly</option>
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label for="packageType">Package Type</Label>
                                <Input
                                    type="select"
                                    id="packageType"
                                    value={editPackage.packageType}
                                    onChange={(e) => setEditPackage({ ...editPackage, packageType: e.target.value })}
                                    className="form-control"
                                >
                                    <option value="TRAIL">Trail</option>
                                    <option value="BASIC">Basic</option>
                                    <option value="PREMIUM">Premium</option>
                                    <option value="ADVANCED">Advanced</option>
                                </Input>
                            </FormGroup>
                        </Form>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleSavePackage} disabled={isLoading}>
                        {isLoading ? 'Updating...' : 'Update'}
                    </Button>
                    <Button color="secondary" onClick={() => setIsEditModalOpen(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

    
            <Modal isOpen={isConfirmModalOpen} toggle={() => setIsConfirmModalOpen(!isConfirmModalOpen)}>
                <ModalHeader toggle={() => setIsConfirmModalOpen(!isConfirmModalOpen)}>
                    Confirm Action
                </ModalHeader>
                <ModalBody>
                    Are you sure you want to {currentPackage && isPackageEnabled(currentPackage.name) ? 'disable' : 'enable'} this package?
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleConfirmAction} disabled={isLoading}>
                        {isLoading ? 'Processing...' : 'Confirm'}
                    </Button>
                    <Button color="secondary" onClick={() => setIsConfirmModalOpen(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default CompanyPermissions;
